import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import PlateformService from '../../components/PlateformService';
import CategorySyncModal from '../../components/CategorySyncModal';
import ProductSyncModal from '../../components/ProductSyncModal';
import GlovoSistersServiceManager from '../../components/GlovoSistersServiceManager';
import {
  fetchAllReviews,
  fetchGlovoAvailabilities,
  syncAddons,
  syncOrders,
  syncOrdersHistory,
  syncProducts,
  syncPromotions,
} from '../../components/GlovoSistersPortalService';

import {
  ensureTokens as ensureSistersTokens,
  testConnection as testSistersConnection,
  syncCategories as sistersSyncCategories,
  getProductsByCategory as sistersGetProductsByCategory,
  syncProductsByCategory as sistersSyncProductsByCategory,
} from '../../components/GlovoSistersService';
import GlovoModal from '../../components/GlovoModal';
import useGlovoModal from '../../components/useGlovoModal';

const statusBadgeClass = (isConnected) =>
  `badge rounded-pill ${isConnected ? 'bg-success' : 'bg-secondary'}`;

const formatAvailabilityLabel = (availabilityState) => {
  if (!availabilityState) return 'Inconnue';
  return availabilityState
    .toString()
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getArrayPayload = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    const value = payload?.[key];
    if (Array.isArray(value)) return value;
  }
  const nested = payload?.response || payload?.data || payload?.result;
  if (nested && nested !== payload) {
    return getArrayPayload(nested, keys);
  }
  return [];
};

const getCategoryCatalogId = (category) => (
  category?._catalogId ||
  category?.catalog_id ||
  category?.catalogId ||
  category?.catalog?.id ||
  category?.catalog?._id ||
  ''
);

function GlovoSistersDashboard() {
  const [loadingPlateforms, setLoadingPlateforms] = useState(false);
  const [plateforms, setPlateforms] = useState([]);
  const [selectedPlateformId, setSelectedPlateformId] = useState('');
  const [loginStatus, setLoginStatus] = useState(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilities, setAvailabilities] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusPayload, setStatusPayload] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsPayload, setReviewsPayload] = useState(null);
  const [error, setError] = useState('');
  const [reviewsData, setReviewsData] = useState([]);
  const [reviewsFromDate, setReviewsFromDate] = useState('');
  const [reviewsToDate, setReviewsToDate] = useState('');
  const [selectedStoresForReviews, setSelectedStoresForReviews] = useState([]);
  const [reviewsFetching, setReviewsFetching] = useState(false);
  const [reviewsStats, setReviewsStats] = useState(null);
  const [allStores, setAllStores] = useState([]);
  const [categorySyncLoading, setCategorySyncLoading] = useState(false);
  const [categorySyncData, setCategorySyncData] = useState(null);
  const [categorySyncError, setCategorySyncError] = useState(null);
  const [selectedStoreForCategorySync, setSelectedStoreForCategorySync] = useState(null);
  const [productSyncLoading, setProductSyncLoading] = useState(false);
  const [productSyncData, setProductSyncData] = useState(null);
  const [productSyncError, setProductSyncError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [promotionsSyncing, setPromotionsSyncing] = useState(false);
  const [promotionsResult, setPromotionsResult] = useState(null);
  const [promotionsError, setPromotionsError] = useState(null);
  const [selectedStoresForPromotions, setSelectedStoresForPromotions] = useState([]);
  const [promotionsStartDate, setPromotionsStartDate] = useState('');
  const [ordersStartDate, setOrdersStartDate] = useState('');
  const [ordersEndDate, setOrdersEndDate] = useState('');
  const [ordersSyncLoading, setOrdersSyncLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  const {
    modalData,
    modalLoading,
    modalError,
    modalTitle,
    closeModal,
    executeWithModal,
  } = useGlovoModal();

  const glovoPlateforms = useMemo(
    () =>
      plateforms.filter(
        (plateform) =>
          (plateform?.type || '').toLowerCase() === 'glovo_sisters' &&
          plateform?.additionnal_data?.isportal === true
      ),
    [plateforms]
  );

  const selectedPlateform = useMemo(
    () => glovoPlateforms.find((plateform) => (plateform?._id || plateform?.id) === selectedPlateformId),
    [glovoPlateforms, selectedPlateformId]
  );

  const hasPortalSession = useMemo(() => {
    if (!selectedPlateform || !selectedPlateform.additionnal_data) return false;
    const { additionnal_data: data } = selectedPlateform;
    return Boolean(
      data.portal?.keymaker?.access_token ||
        data.portal?.refresh_token ||
        data.glovo_portal_bearer ||
        data.portal_access_token
    );
  }, [selectedPlateform]);

  const glovoOptions = useMemo(
    () =>
      glovoPlateforms.map((plateform) => {
        const value = plateform._id || plateform.id;
        return {
          value,
          label: plateform.name || plateform.login || value,
        };
      }),
    [glovoPlateforms]
  );

  const mappedStoreInfo = useMemo(() => {
    const mapping = selectedPlateform?.additionnal_data?.stores_maped;
    if (!mapping) return null;
    if (Array.isArray(mapping)) {
      const mapped = mapping[0];
      if (!mapped) return null;
      return {
        storeId: mapped.store_id || mapped.storeId || null,
        vendorId: mapped.remote_store_id || mapped.vendorId || mapped.vendor_id || null,
      };
    }
    return {
      storeId: mapping.store_id || mapping.storeId || null,
      vendorId: mapping.remote_store_id || mapping.vendorId || mapping.vendor_id || null,
    };
  }, [selectedPlateform]);

  const loadPlateforms = async () => {
    try {
      setLoadingPlateforms(true);
      const data = await PlateformService.fetchPlateforms();
      const list = data.response || data;
      setPlateforms(Array.isArray(list) ? list : []);
    } catch (fetchError) {
      console.error(fetchError);
      setError(
        "Impossible de charger les plateformes Glovo Sisters. Vérifiez votre connexion ou vos permissions."
      );
    } finally {
      setLoadingPlateforms(false);
    }
  };

  useEffect(() => {
    loadPlateforms();

    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 7);
    setReviewsToDate(today.toISOString().split('T')[0]);
    setReviewsFromDate(weekAgo.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    const loadStores = async () => {
      if (!selectedPlateform || !selectedPlateform.store_ids || selectedPlateform.store_ids.length === 0) {
        setAllStores([]);
        setSelectedStoresForReviews([]);
        return;
      }

      try {
        const axios = (await import('axios')).default;
        const { header } = await import('../../components/header.encry');
        const API_URL = process.env.REACT_APP_PEGASUS_API_URL;
        const response = await axios.get(`${API_URL}/stores`, header());
        const stores = response.data.response || response.data;

        const platformStores = stores.filter(store => 
          selectedPlateform.store_ids.some(id => 
            (id.$oid || id) === (store._id || store.id)
          )
        );

        // Attach remote mapping if available in platform.additional_data.stores_maped
        const mapping = selectedPlateform.additionnal_data?.stores_maped;

        const mappedStores = platformStores.map(store => {
          const storeId = store._id || store.id;
          let remoteId = null;

          if (mapping) {
            if (Array.isArray(mapping)) {
              const m = mapping.find(m => m.store_id === storeId || (m.store_id && (m.store_id.$oid || m.store_id) === storeId));
              if (m) remoteId = m.remote_store_id || m.vendorId || m.vendor_id;
            } else if (typeof mapping === 'object') {
              // Single mapping object
              if (mapping.store_id === storeId || (mapping.store_id && (mapping.store_id.$oid || mapping.store_id) === storeId)) {
                remoteId = mapping.remote_store_id || mapping.vendorId || mapping.vendor_id;
              }
            }
          }

          return {
            ...store,
            remote_store_id: remoteId || store.storeCode || store.global_vendor_id || null
          };
        });

        setAllStores(mappedStores);
      } catch (err) {
        console.error('Error loading stores:', err);
      }
    };

    loadStores();
  }, [selectedPlateform]);

  useEffect(() => {
    if (!mappedStoreInfo?.storeId || allStores.length === 0) return;
    const mappedStore = allStores.find(
      (store) => (store._id || store.id) === mappedStoreInfo.storeId
    );
    if (mappedStore) {
      setSelectedStoreForCategorySync({
        value: mappedStore._id || mappedStore.id,
        label: `${mappedStore.name} (${mappedStore.remote_store_id || mappedStore.storeCode || ''})`,
        storeCode: mappedStore.storeCode,
        remote_store_id: mappedStore.remote_store_id,
      });
    }
  }, [allStores, mappedStoreInfo]);

  useEffect(() => {
    if (glovoOptions.length > 0 && !selectedPlateformId) {
      setSelectedPlateformId(glovoOptions[0].value);
    }
  }, [glovoOptions, selectedPlateformId]);

  // Auto-sync orders every 5 minutes
  const handleSelectPlateform = (option) => {
    setSelectedPlateformId(option?.value || '');
    setLoginStatus(null);
    setAvailabilities([]);
    setError('');
  };

  const handleLogin = async () => {
    if (!selectedPlateformId) return;
    try {
      setLoginLoading(true);
      setError('');
      const response = await ensureSistersTokens({ 
        plateformId: selectedPlateformId,
        forceLogin: true 
      });
      setLoginStatus({
        source: response?.tokens?.source || 'login',
        plateformId: response?.plateformId || selectedPlateformId,
      });
      await loadPlateforms();
    } catch (loginError) {
      console.error(loginError);
      const message =
        loginError?.response?.data?.error ||
        loginError?.response?.data?.message ||
        loginError.message ||
        'Échec de la connexion au portail Glovo.';
      setError(message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleFetchAvailabilities = async () => {
    if (!selectedPlateformId) return;
    try {
      setAvailabilityLoading(true);
      setError('');
      const response = await fetchGlovoAvailabilities({ plateformId: selectedPlateformId });
      setAvailabilities(response?.availabilities || []);
      setLoginStatus((prev) => ({
        ...prev,
        source: response?.source || prev?.source,
      }));
    } catch (availabilityError) {
      console.error(availabilityError);
      const message =
        availabilityError?.response?.data?.error ||
        availabilityError?.response?.data?.message ||
        availabilityError.message ||
        'Impossible de récupérer les disponibilités Glovo.';
      setError(message);
    } finally {
      setAvailabilityLoading(false);
    }
  };

  // New modal handlers using the hook
  const handleSyncReviews = async () => {
    if (!selectedPlateformId) return;
    
    // Calculate date range (last 7 days by default)
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(toDate.getDate() - 7);
    
    executeWithModal(
      'Récupération des Avis Glovo (7 derniers jours)',
      () => fetchAllReviews({ 
        plateformId: selectedPlateformId,
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
        perPage: 50,
        hasText: false
      })
    );
  };

  // Handler for dedicated reviews section
  const handleFetchReviewsSection = async () => {
    if (!selectedPlateformId) return;
    
    setReviewsFetching(true);
    setError('');
    
    try {
      const globalVendorCodes = selectedStoresForReviews.length > 0
        ? selectedStoresForReviews.map(s => s.remote_store_id || s.storeCode).filter(Boolean)
        : undefined;

      const fromDateISO = reviewsFromDate ? new Date(reviewsFromDate).toISOString() : undefined;
      const toDateISO = reviewsToDate ? new Date(reviewsToDate + 'T23:59:59').toISOString() : undefined;

      const result = await fetchAllReviews({
        plateformId: selectedPlateformId,
        globalVendorCodes,
        fromDate: fromDateISO,
        toDate: toDateISO,
        perPage: 50,
        hasText: false
      });

      setReviewsData(result.reviews || []);
      
      // Calculate stats
      if (result.reviews && result.reviews.length > 0) {
        const ratings = result.reviews.map(r => r.rating || 0);
        const avgRating = ratings.reduce((a, b) => a + b, 0) / ratings.length;
        const withText = result.reviews.filter(r => r.text && r.text.trim()).length;

        setReviewsStats({
          total: result.total,
          pages: result.pages,
          avgRating: avgRating.toFixed(2),
          withText,
          fiveStars: result.reviews.filter(r => r.rating === 5).length,
          fourStars: result.reviews.filter(r => r.rating === 4).length,
          threeStars: result.reviews.filter(r => r.rating === 3).length,
          twoStars: result.reviews.filter(r => r.rating === 2).length,
          oneStar: result.reviews.filter(r => r.rating === 1).length
        });
      } else {
        setReviewsStats(null);
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError(err?.response?.data?.error || err.message || 'Erreur lors de la récupération des avis');
    } finally {
      setReviewsFetching(false);
    }
  };

  const handleSyncCategoriesPortal = async () => {
    if (!selectedPlateformId || !selectedPlateform) return;
    
    // Check if a store is selected
    if (!selectedStoreForCategorySync && !mappedStoreInfo?.storeId) {
      setError('Veuillez sélectionner un magasin pour synchroniser les catégories');
      return;
    }

    const selectedStoreId = mappedStoreInfo?.storeId || selectedStoreForCategorySync?.value;
    // Find the actual store object from allStores to get the storeCode
    const selectedStore = allStores.find(store => 
      (store._id || store.id) === selectedStoreId
    );

    console.log('Debug - Selected store object:', selectedStore);
    console.log('Debug - selectedStoreForCategorySync:', selectedStoreForCategorySync);

    if (!selectedStore) {
      setError('Magasin introuvable');
      return;
    }

    // Use the mapped remote_store_id as vendorId when available
    const vendorId = mappedStoreInfo?.vendorId || selectedStore.remote_store_id || selectedStore.storeCode;
    if (!vendorId) {
      setError('VendorId (remote_store_id) non trouvé dans le magasin sélectionné');
      return;
    }

    console.log('Syncing categories with:', {
      plateformId: selectedPlateformId,
      vendorId: vendorId,
      storeId: selectedStoreId,
      storeName: selectedStore.name,
      fullStoreObject: selectedStore
    });

    setCategorySyncLoading(true);
    setCategorySyncError(null);
    setCategorySyncData(null);
    setError('');

    // Open modal with Bootstrap (globally available)
    const modalElement = document.getElementById('categorySyncModal');
    if (modalElement && window.bootstrap) {
      const modal = new window.bootstrap.Modal(modalElement);
      modal.show();
    }

    try {
      const result = await sistersSyncCategories({
        plateformId: selectedPlateformId,
        vendorId: vendorId,
        globalEntityId: 'GV_MA',
        storeId: selectedStoreId
      });

      console.log('Category sync result:', result);
      
      // Ensure we have the categories array, even when the API wraps it.
      const categories = getArrayPayload(result, ['categories', 'items', 'list']);
      const categoriesData = {
        ...result,
        categories
      };
      
      setCategorySyncData(categoriesData);
    } catch (err) {
      console.error('Error syncing categories:', err);
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || err.message || 'Erreur lors de la synchronisation des catégories';
      setCategorySyncError(errorMessage);
    } finally {
      setCategorySyncLoading(false);
    }
  };

  const handleCategoryClick = async (category) => {
    if (!selectedPlateformId || !selectedPlateform || !category) return;
    
    // Determine vendorId from selected store mapping or platform mapping
    let vendorId = mappedStoreInfo?.vendorId || null;
    if (!vendorId && selectedStoreForCategorySync) {
      const storeObj = allStores.find(s => (s._id || s.id) === selectedStoreForCategorySync.value);
      vendorId = storeObj?.remote_store_id || storeObj?.storeCode || null;
    }

    if (!vendorId) {
      setError('Vendor ID non trouvé dans la plateforme sélectionnée');
      return;
    }

    setSelectedCategory(category);
    setProductSyncLoading(true);
    setProductSyncError(null);
    setProductSyncData(null);

    // Open product modal
    const modalElement = document.getElementById('productSyncModal');
    if (modalElement && window.bootstrap) {
      const modal = new window.bootstrap.Modal(modalElement);
      modal.show();
    }

    try {
      // First get the products from the category
      const productsResult = await sistersGetProductsByCategory({
        plateformId: selectedPlateformId,
        vendorId: vendorId,
        catalogId: category.catalog_id || category.catalogId,
        categoryId: category.remote_plateform_id || category.id,
        globalEntityId: 'GV_MA'
      });

      // Then sync the products via sisters endpoint
      const syncResult = await sistersSyncProductsByCategory({
        plateformId: selectedPlateformId,
        vendorId: vendorId,
        categoryId: category.remote_plateform_id || category.id,
        globalEntityId: 'GV_MA',
        storeCode: vendorId, // ensure storeCode equals vendorId as required by the API
        catalogId: getCategoryCatalogId(category) || null
      });

      setProductSyncData({
        ...syncResult,
        products: productsResult.products || []
      });
    } catch (err) {
      console.error('Error syncing products:', err);
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || err.message || 'Erreur lors de la synchronisation des produits';
      setProductSyncError(errorMessage);
    } finally {
      setProductSyncLoading(false);
    }
  };

  const handleSyncProducts = () => {
    if (!selectedPlateformId) return;
    executeWithModal(
      'Synchronisation des Produits',
      () => syncProducts({ plateformId: selectedPlateformId })
    );
  };

  const handleSyncOrders = () => {
    if (!selectedPlateformId) return;
    executeWithModal(
      'Synchronisation des Commandes (4 derniers jours)',
      () => syncOrders({ 
        plateformId: selectedPlateformId,
        days: 4
      })
    );
  };

  const handleSyncOrdersHistory = () => {
    if (!selectedPlateformId) return;
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7); // Last 7 days
    
    executeWithModal(
      'Synchronisation Historique des Commandes',
      () => syncOrdersHistory({ 
        plateformId: selectedPlateformId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      })
    );
  };

  const handleSyncOrdersByDate = async () => {
    if (!selectedPlateformId) return;
    if (!ordersStartDate || !ordersEndDate) {
      // simple validation
      alert('Veuillez sélectionner une date de début et une date de fin.');
      return;
    }

    const payload = {
      plateformId: selectedPlateformId,
      timeFrom: new Date(ordersStartDate).toISOString(),
      timeTo: new Date(ordersEndDate).toISOString(),
    };

    setOrdersSyncLoading(true);
    try {
      await executeWithModal('Synchronisation Commandes (par date)', () => syncOrders(payload));
    } catch (err) {
      console.error('Error syncing orders by date:', err);
    } finally {
      setOrdersSyncLoading(false);
    }
  };

  const handleSyncAddons = () => {
    if (!selectedPlateformId) return;
    executeWithModal(
      'Synchronisation des Addons',
      () => syncAddons({ plateformId: selectedPlateformId })
    );
  };

  const handleTestConnection = () => {
    if (!selectedPlateformId) return;
    executeWithModal(
      'Test de Connexion Glovo',
      () => testSistersConnection({ plateformId: selectedPlateformId })
    );
  };

  return (
    <div className="container-fluid p-4">
      <div className="d-flex align-items-center mb-4">
        <div>
          <h3 className="mb-1">Plateforme Glovo Sisters</h3>
          <p className="mb-0 text-muted">
            Gérer la connexion au portail Glovo Sisters et consulter la disponibilité des restaurants.
          </p>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex flex-wrap align-items-end gap-3">
            <div className="flex-grow-1" style={{ minWidth: '260px' }}>
              <label className="form-label">Plateforme Glovo Sisters</label>
              <Select
                options={glovoOptions}
                value={glovoOptions.find((option) => option.value === selectedPlateformId) || null}
                onChange={handleSelectPlateform}
                isLoading={loadingPlateforms}
                placeholder="Sélectionnez une plateforme Glovo Sisters"
                noOptionsMessage={() =>
                  loadingPlateforms
                    ? 'Chargement des plateformes…'
                    : 'Aucune plateforme Glovo Sisters disponible'
                }
              />
            </div>

            {!hasPortalSession && (
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handleLogin}
                disabled={loginLoading || !selectedPlateformId}
              >
                {loginLoading ? 'Connexion…' : 'Se connecter au portail'}
              </button>
            )}

            {hasPortalSession && (
              <div className="d-flex align-items-center gap-2">
                <span className={statusBadgeClass(true)}>Connecté</span>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleLogin}
                  disabled={loginLoading || !selectedPlateformId}
                >
                  {loginLoading ? 'Actualisation…' : 'Rafraîchir la connexion'}
                </button>
              </div>
            )}
          </div>

          <div className="mt-3 d-flex flex-wrap align-items-center gap-3">
            <div>
              <span className={statusBadgeClass(hasPortalSession)}>
                {hasPortalSession ? 'Session portail disponible' : 'Non connecté'}
              </span>
            </div>
            {loginStatus?.source && (
              <small className="text-muted">
                Source de la session : <strong>{loginStatus.source}</strong>
              </small>
            )}
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            <i className="fas fa-tachometer-alt me-2"></i>
            Dashboard
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'services' ? 'active' : ''}`}
            onClick={() => setActiveTab('services')}
          >
            <i className="fas fa-cogs me-2"></i>
            Gestionnaire de Services
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'reviews' ? 'active' : ''}`}
            onClick={() => setActiveTab('reviews')}
          >
            <i className="fas fa-star me-2"></i>
            Avis Clients
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'promotions' ? 'active' : ''}`}
            onClick={() => setActiveTab('promotions')}
          >
            <i className="fas fa-tags me-2"></i>
            Promotions
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            <i className="fas fa-shopping-cart me-2"></i>
            Commandes
          </button>
        </li>
      </ul>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
      <div className="card">
        <div className="card-body">
          {/* Category Sync Section */}
          <div className="mb-4 p-3 bg-light rounded">
            <h6 className="mb-3">
              <i className="fas fa-folder me-2"></i>
              Gestion des Catégories
            </h6>
            <div className="row g-3 align-items-end">
              <div className="col-md-6">
                <label className="form-label">Sélectionner un magasin</label>
                <Select
                  options={allStores.map(store => ({
                      value: store._id || store.id,
                      label: `${store.name} (${store.remote_store_id || store.storeCode || ''})`,
                      storeCode: store.storeCode,
                      remote_store_id: store.remote_store_id
                    }))}
                  value={selectedStoreForCategorySync}
                  onChange={(selected) => setSelectedStoreForCategorySync(selected)}
                  placeholder="Choisir un magasin..."
                  isClearable
                  isSearchable
                  isDisabled={!selectedPlateformId || allStores.length === 0}
                />
                {selectedStoreForCategorySync && (() => {
                  const store = allStores.find(s => (s._id || s.id) === selectedStoreForCategorySync.value);
                  return store ? (
                    <small className="text-muted">
                      VendorId (remote): {store.remote_store_id || store.storeCode}
                    </small>
                  ) : null;
                })()}
              </div>
              <div className="col-md-6">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSyncCategoriesPortal}
                  disabled={!selectedPlateformId || !selectedStoreForCategorySync}
                >
                  <i className="fas fa-sync me-2"></i>
                  Synchroniser les catégories
                </button>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          <div className="d-flex flex-wrap align-items-center mb-3 gap-3">
            <div>
              <h5 className="mb-0">Disponibilités par store</h5>
              <small className="text-muted">
                Rafraîchissez pour récupérer l'état actuel des restaurants liés à cette plateforme.
              </small>
            </div>
            <div className="ms-auto d-flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleTestConnection}
                disabled={!selectedPlateformId}
              >
                Test Connexion
              </button>
              <button
                type="button"
                className="btn btn-outline-info btn-sm"
                onClick={handleSyncReviews}
                disabled={!selectedPlateformId}
              >
                Sync Avis
              </button>
              <button
                type="button"
                className="btn btn-outline-info btn-sm"
                onClick={handleSyncProducts}
                disabled={!selectedPlateformId}
              >
                Sync Produits
              </button>
              <button
                type="button"
                className="btn btn-outline-info btn-sm"
                onClick={handleSyncOrders}
                disabled={!selectedPlateformId}
              >
                Sync Commandes
              </button>
              <button
                type="button"
                className="btn btn-outline-warning btn-sm"
                onClick={handleSyncOrdersHistory}
                disabled={!selectedPlateformId}
              >
                Historique Commandes
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleSyncAddons}
                disabled={!selectedPlateformId}
              >
                Sync Addons
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleFetchAvailabilities}
                disabled={availabilityLoading || !selectedPlateformId}
              >
                {availabilityLoading ? 'Chargement…' : 'Disponibilités'}
              </button>
            </div>
          </div>

          {availabilityLoading && (
            <div className="text-center py-3">Chargement des disponibilités…</div>
          )}

          {!availabilityLoading && availabilities.length === 0 && (
            <div className="alert alert-info" role="alert">
              Aucune disponibilité à afficher. Sélectionnez une plateforme Glovo puis cliquez sur "Disponibilités".
            </div>
          )}

          {!availabilityLoading && availabilities.length > 0 && (
            <div className="table-responsive">
              <table className="table table-striped align-middle">
                <thead>
                  <tr>
                    <th scope="col">Restaurant</th>
                    <th scope="col">État</th>
                    <th scope="col">Modifiable</th>
                    <th scope="col">Prochaine ouverture</th>
                    <th scope="col">Clôture actuelle</th>
                    <th scope="col">Global Entity</th>
                  </tr>
                </thead>
                <tbody>
                  {availabilities.map((item, index) => (
                    <tr key={`${item.platformRestaurantId || index}-${index}`}>
                      <td>
                        <div className="fw-semibold">{item.name || item.platformRestaurantId || 'Restaurant'}</div>
                        <div className="text-muted small">{item.platformKey || ''}</div>
                      </td>
                      <td>{formatAvailabilityLabel(item.availabilityState)}</td>
                      <td>{item.changeable ? 'Oui' : 'Non'}</td>
                      <td>
                        {item.closedUntil
                          ? new Date(item.closedUntil).toLocaleString()
                          : item.currentSlotEndAt
                          ? new Date(item.currentSlotEndAt).toLocaleString()
                          : '—'}
                      </td>
                      <td>
                        {item.checkoutAt
                          ? new Date(item.checkoutAt).toLocaleString()
                          : item.closedReason || '—'}
                      </td>
                      <td>{item.globalEntityId || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Services Tab */}
      {activeTab === 'services' && selectedPlateformId && selectedPlateform && (
        <div className="card">
          <div className="card-header bg-light">
            <h5 className="mb-0">
              <i className="fas fa-cogs me-2"></i>
              Gestionnaire de Services Glovo Portal
            </h5>
          </div>
          <div className="card-body p-0">
            <GlovoSistersServiceManager
              plateformId={selectedPlateformId}
              selectedPlateform={selectedPlateform}
            />
          </div>
        </div>
      )}

      {activeTab === 'services' && !selectedPlateformId && (
        <div className="alert alert-info">
          <i className="fas fa-info-circle me-2"></i>
          Veuillez sélectionner une plateforme Glovo pour accéder aux services
        </div>
      )}

      {/* Reviews Tab */}
      {activeTab === 'reviews' && (
      <div className="card">
        <div className="card-header bg-light">
          <h5 className="mb-0">
            <i className="fas fa-star me-2"></i>
            Avis Clients Glovo
          </h5>
        </div>
        <div className="card-body">
          {/* Filters */}
          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <label className="form-label">Magasins</label>
              <Select
                isMulti
                options={allStores.map(store => ({
                  value: store._id || store.id,
                  label: store.name,
                  storeCode: store.storeCode,
                  remote_store_id: store.remote_store_id
                }))}
                value={allStores
                  .map(store => ({
                    value: store._id || store.id,
                    label: store.name,
                    storeCode: store.storeCode,
                    remote_store_id: store.remote_store_id
                  }))
                  .filter(opt => selectedStoresForReviews.some(s => s.value === opt.value))}
                onChange={(selected) => setSelectedStoresForReviews(selected || [])}
                placeholder="Tous les magasins..."
                isClearable
                isSearchable
                isDisabled={!selectedPlateformId || allStores.length === 0}
              />
              <small className="text-muted">
                Laissez vide pour tous les magasins
              </small>
            </div>
            <div className="col-md-3">
              <label className="form-label">Date de début</label>
              <input
                type="date"
                className="form-control"
                value={reviewsFromDate}
                onChange={(e) => setReviewsFromDate(e.target.value)}
                disabled={!selectedPlateformId}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Date de fin</label>
              <input
                type="date"
                className="form-control"
                value={reviewsToDate}
                onChange={(e) => setReviewsToDate(e.target.value)}
                disabled={!selectedPlateformId}
              />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-primary w-100"
                onClick={handleFetchReviewsSection}
                disabled={reviewsFetching || !selectedPlateformId}
              >
                {reviewsFetching ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Chargement...
                  </>
                ) : (
                  <>
                    <i className="fas fa-download me-2"></i>
                    Charger
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Statistics */}
          {reviewsStats && (
            <div className="row mb-3">
              <div className="col-md-2">
                <div className="card bg-primary text-white">
                  <div className="card-body p-2">
                    <h6 className="mb-1">Total</h6>
                    <h3 className="mb-0">{reviewsStats.total}</h3>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-warning text-dark">
                  <div className="card-body p-2">
                    <h6 className="mb-1">Moyenne</h6>
                    <h3 className="mb-0">
                      {reviewsStats.avgRating} <i className="fas fa-star"></i>
                    </h3>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="card bg-info text-white">
                  <div className="card-body p-2">
                    <h6 className="mb-1">Avec texte</h6>
                    <h3 className="mb-0">{reviewsStats.withText}</h3>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card">
                  <div className="card-body p-2">
                    <h6 className="mb-2">Distribution</h6>
                    <div className="d-flex justify-content-around">
                      <div className="text-center">
                        <div className="small"><i className="fas fa-star text-warning"></i> 5</div>
                        <strong>{reviewsStats.fiveStars}</strong>
                      </div>
                      <div className="text-center">
                        <div className="small"><i className="fas fa-star text-warning"></i> 4</div>
                        <strong>{reviewsStats.fourStars}</strong>
                      </div>
                      <div className="text-center">
                        <div className="small"><i className="fas fa-star text-warning"></i> 3</div>
                        <strong>{reviewsStats.threeStars}</strong>
                      </div>
                      <div className="text-center">
                        <div className="small"><i className="fas fa-star text-warning"></i> 2</div>
                        <strong>{reviewsStats.twoStars}</strong>
                      </div>
                      <div className="text-center">
                        <div className="small"><i className="fas fa-star text-warning"></i> 1</div>
                        <strong>{reviewsStats.oneStar}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reviews Table */}
          {reviewsData.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover table-striped">
                <thead className="table-dark">
                  <tr>
                    <th>Date</th>
                    <th>Note</th>
                    <th>Client</th>
                    <th>Magasin</th>
                    <th>Commentaire</th>
                    <th>Commande</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewsData.map((review, index) => (
                    <tr key={index}>
                      <td>
                        <small>
                          {new Date(review.createdAt || review.date).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </small>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <strong className="me-2">{review.rating}</strong>
                          {[...Array(5)].map((_, i) => (
                            <i
                              key={i}
                              className={`fas fa-star ${
                                i < review.rating ? 'text-warning' : 'text-muted'
                              }`}
                              style={{ fontSize: '0.8rem' }}
                            ></i>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div>
                          <strong>{review.customerName || 'Anonyme'}</strong>
                        </div>
                        {review.customerEmail && (
                          <small className="text-muted">{review.customerEmail}</small>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">
                          {review.storeCode || review.globalVendorCode || '-'}
                        </small>
                      </td>
                      <td>
                        {review.text ? (
                          <div style={{ maxWidth: '300px' }}>
                            {review.text}
                          </div>
                        ) : (
                          <span className="text-muted fst-italic">Pas de commentaire</span>
                        )}
                      </td>
                      <td>
                        <small className="text-muted">
                          {review.orderId || '-'}
                        </small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : !reviewsFetching ? (
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              {selectedPlateformId 
                ? 'Cliquez sur "Charger" pour récupérer les avis' 
                : 'Sélectionnez une plateforme pour commencer'}
            </div>
          ) : null}
        </div>
      </div>
      )}

      {/* Promotions Tab */}
      {activeTab === 'promotions' && selectedPlateformId && selectedPlateform && (
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-tags me-2"></i>
              Synchronisation des Promotions
            </h5>
          </div>
          <div className="card-body">
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              Synchronisez les promotions depuis Glovo Portal vers votre base de données locale (max 6 mois en arrière).
            </div>

            {/* Store Selection */}
            <div className="mb-3">
              <label className="form-label">
                <i className="fas fa-store me-2"></i>
                Sélectionnez les stores
              </label>
              <Select
                isMulti
                options={allStores.map(store => ({
                  value: store._id || store.id,
                  label: store.name,
                  storeCode: store.storeCode,
                  rawId: store._id || store.id,
                  remote_store_id: store.remote_store_id
                }))}
                value={selectedStoresForPromotions}
                onChange={setSelectedStoresForPromotions}
                placeholder="Choisir des stores..."
                className="basic-multi-select"
                classNamePrefix="select"
                isClearable
                isSearchable
                isDisabled={!selectedPlateformId || allStores.length === 0}
              />
              <small className="text-muted">
                Laissez vide pour synchroniser tous les stores de cette plateforme ({allStores.length} store(s) disponible(s))
              </small>
            </div>

            {/* Date Selection */}
            <div className="mb-3">
              <label className="form-label">
                <i className="fas fa-calendar me-2"></i>
                Date de début
              </label>
              <input
                type="date"
                className="form-control"
                value={promotionsStartDate}
                onChange={(e) => setPromotionsStartDate(e.target.value)}
                min={(() => {
                  const sixMonthsAgo = new Date();
                  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
                  return sixMonthsAgo.toISOString().split('T')[0];
                })()}
                max={new Date().toISOString().split('T')[0]}
                required
              />
              <small className="text-muted">
                Sélectionnez la date de début (maximum 6 mois en arrière)
              </small>
            </div>

            <button
              className="btn btn-primary"
              onClick={async () => {
                setPromotionsSyncing(true);
                setPromotionsError(null);
                setPromotionsResult(null);
                
                try {
                  // Get vendor codes from selected stores or all platform stores
                  let vendorCodes = [];
                  
                  if (selectedStoresForPromotions.length > 0) {
                    vendorCodes = selectedStoresForPromotions
                      .map(store => store.remote_store_id || store.storeCode)
                      .filter(Boolean);
                  } else {
                    // Use all stores from platform
                    vendorCodes = allStores
                      .map(store => store.remote_store_id || store.storeCode)
                      .filter(Boolean);
                  }

                  if (vendorCodes.length === 0) {
                    setPromotionsError('Aucun store avec un storeCode trouvé');
                    setPromotionsSyncing(false);
                    return;
                  }

                  // Validate start date
                  if (!promotionsStartDate) {
                    setPromotionsError('Veuillez sélectionner une date de début');
                    setPromotionsSyncing(false);
                    return;
                  }

                  // Calculate end date: 6 months from start date or today (whichever is earlier)
                  const startDateObj = new Date(promotionsStartDate);
                  const sixMonthsLater = new Date(startDateObj);
                  sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
                  const today = new Date();
                  const endDateObj = sixMonthsLater < today ? sixMonthsLater : today;

                  const formatDate = (date) => {
                    if (typeof date === 'string') return date; // Already formatted
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  };

                  const payload = {
                    plateformId: selectedPlateformId,
                    storeId: vendorCodes[0], // First vendor code as store reference
                    vendorCodes: vendorCodes, // REQUIRED: Array of vendor codes
                    globalEntityId: 'GV_MA',
                    startDate: promotionsStartDate,
                    endDate: formatDate(endDateObj),
                    pageSize: 500,
                    pageOffset: 0,
                    sortByField: 'startDate',
                    sortOrder: 'desc',
                    includeCofundedDeal: true,
                    includeMembershipDeal: true,
                    includeJokerDeal: false,
                    includePlatformEventDeal: false,
                    includeBoostedDeal: false,
                    includeDeliveryFeeDeal: true,
                  };

                  const result = await syncPromotions(payload);
                  setPromotionsResult(result);
                } catch (err) {
                  console.error('Error syncing promotions:', err);
                  setPromotionsError(err.message || 'Erreur lors de la synchronisation');
                } finally {
                  setPromotionsSyncing(false);
                }
              }}
              disabled={promotionsSyncing}
            >
              {promotionsSyncing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Synchronisation en cours...
                </>
              ) : (
                <>
                  <i className="fas fa-sync me-2"></i>
                  Synchroniser les Promotions
                </>
              )}
            </button>

            {promotionsError && (
              <div className="alert alert-danger mt-3">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {promotionsError}
              </div>
            )}

            {promotionsResult && (
              <div className="alert alert-success mt-3">
                <h6>
                  <i className="fas fa-check-circle me-2"></i>
                  Synchronisation réussie
                </h6>
                <ul className="mb-0">
                  <li>Total récupéré: {promotionsResult.stats?.totalFetched || 0}</li>
                  <li>Créées: {promotionsResult.stats?.created || 0}</li>
                  <li>Mises à jour: {promotionsResult.stats?.updated || 0}</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'promotions' && !selectedPlateformId && (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Veuillez sélectionner une plateforme pour synchroniser les promotions.
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && selectedPlateformId && selectedPlateform && (
        <div className="card">
          <div className="card-header">
            <h5 className="mb-0">
              <i className="fas fa-shopping-cart me-2"></i>
              Gestion des Commandes
            </h5>
          </div>
          <div className="card-body">
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <label className="form-label">Date de début</label>
                <input
                  type="date"
                  className="form-control"
                  value={ordersStartDate}
                  onChange={(e) => setOrdersStartDate(e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Date de fin</label>
                <input
                  type="date"
                  className="form-control"
                  value={ordersEndDate}
                  onChange={(e) => setOrdersEndDate(e.target.value)}
                />
              </div>
              <div className="col-md-4 d-flex align-items-end">
                <button
                  className="btn btn-primary w-100"
                  onClick={handleSyncOrdersByDate}
                  disabled={ordersSyncLoading}
                >
                  {ordersSyncLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Synchronisation...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-download me-2"></i>
                      Sync Commandes (par date)
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Sync Modal */}
      <CategorySyncModal
        modalId="categorySyncModal"
        isLoading={categorySyncLoading}
        categories={categorySyncData?.categories || []}
        syncStats={categorySyncData}
        error={categorySyncError}
        onCategoryClick={handleCategoryClick}
      />

      {/* Product Sync Modal */}
      <ProductSyncModal
        modalId="productSyncModal"
        isLoading={productSyncLoading}
        products={productSyncData?.products || []}
        syncStats={productSyncData}
        error={productSyncError}
        categoryName={selectedCategory?.name || ''}
      />

      {/* Glovo Modal for displaying API results */}
      <GlovoModal
        modalId="glovoModal"
        title={modalTitle}
        data={modalData}
        loading={modalLoading}
        error={modalError}
        onClose={closeModal}
      />
    </div>
  );
}

export default GlovoSistersDashboard;
